import {
  Group, Text, Button, Avatar, Menu, Burger, Drawer, NavLink,
  Badge, UnstyledButton, Flex, ActionIcon, useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
  Home, BookOpen, BarChart3, Users, ClipboardList,
  CheckSquare, Layers, LogOut, User, Zap, BookA, Trophy, Calendar,
  Sun, Moon, RefreshCw, ChevronDown, ListChecks, CreditCard,
} from 'lucide-react'

interface NavItem { label: string; path: string; icon: React.ReactNode; children?: NavItem[] }

const studentLinks: NavItem[] = [
  { label: 'Home', path: '/dashboard', icon: <Home size={16} /> },
  { label: 'Subscription', path: '/subscription', icon: <CreditCard size={16} /> },
  {
    label: 'Vocabulary', path: '/vocabulary', icon: <BookA size={16} />,
    children: [
      { label: 'Learning', path: '/vocabulary', icon: <BookOpen size={16} /> },
      { label: 'Review', path: '/vocabulary/review', icon: <RefreshCw size={16} /> },
      { label: 'Progress', path: '/vocabulary/progress', icon: <BarChart3 size={16} /> },
      { label: 'Test', path: '/vocabulary/test', icon: <ListChecks size={16} /> },
    ],
  },
  { label: 'Study Plan', path: '/study-plan', icon: <Calendar size={16} /> },
 /*  { label: 'Leaderboard', path: '/leaderboard', icon: <Trophy size={16} /> }, */
]

const tutorLinks: NavItem[] = [
  { label: 'Dashboard', path: '/tutor/dashboard', icon: <Home size={16} /> },
  { label: 'Subscription', path: '/subscription', icon: <CreditCard size={16} /> },
  { label: 'Rosters', path: '/tutor/rosters', icon: <Users size={16} /> },
  { label: 'Assignments', path: '/tutor/assignments', icon: <ClipboardList size={16} /> },
  { label: 'Grading', path: '/tutor/grading', icon: <CheckSquare size={16} /> },
]

const adminLinks: NavItem[] = [
  { label: 'Dashboard', path: '/admin/dashboard', icon: <Home size={16} /> },
  { label: 'Subscription', path: '/subscription', icon: <CreditCard size={16} /> },
  { label: 'SAT Plans', path: '/admin/sat-plans', icon: <CreditCard size={16} /> },
  { label: 'Question Bank', path: '/admin/questions', icon: <BookOpen size={16} /> },
  { label: 'Vocabulary', path: '/admin/vocabulary', icon: <BookA size={16} /> },
  { label: 'Users', path: '/admin/users', icon: <Users size={16} /> },
  { label: 'Content', path: '/admin/content', icon: <Layers size={16} /> },
]

function getLinksForRole(role?: string): NavItem[] {
  if (role === 'student') return studentLinks
  if (role === 'tutor') return tutorLinks
  if (role === 'god') return adminLinks
  return []
}

export function NavBar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false)
  const { toggleColorScheme } = useMantineColorScheme()
  const computed = useComputedColorScheme('dark')

  const links = getLinksForRole(user?.role)
  const isActive = (path: string) => location.pathname === path
  const isGroupActive = (link: NavItem) =>
    link.children ? link.children.some((c) => isActive(c.path)) : isActive(link.path)

  const desktopLinks = links.map((link) => {
    if (link.children) {
      const active = isGroupActive(link)
      return (
        <Menu key={link.path} shadow="md" width={180} position="bottom-start">
          <Menu.Target>
            <UnstyledButton
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 12px',
                borderBottom: active ? '2px solid var(--sat-red)' : '2px solid transparent',
                color: active ? 'var(--sat-red)' : 'var(--sat-green)',
                fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap',
              }}
            >
              {link.icon}{link.label}<ChevronDown size={13} style={{ marginLeft: 2 }} />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {link.children.map((child) => (
              <Menu.Item
                key={child.path}
                leftSection={child.icon}
                onClick={() => navigate(child.path)}
                style={{ color: isActive(child.path) ? 'var(--sat-red)' : undefined, fontWeight: isActive(child.path) ? 600 : undefined }}
              >
                {child.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )
    }
    return (
      <UnstyledButton
        key={link.path}
        onClick={() => navigate(link.path)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 12px',
          borderBottom: isActive(link.path) ? '2px solid var(--sat-red)' : '2px solid transparent',
          color: isActive(link.path) ? 'var(--sat-red)' : 'var(--sat-green)',
          fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap',
        }}
      >
        {link.icon}{link.label}
      </UnstyledButton>
    )
  })

  const mobileLinks = links.map((link) => {
    if (link.children) {
      return (
        <NavLink
          key={link.path}
          label={link.label}
          leftSection={link.icon}
          active={isGroupActive(link)}
          color="grapefruit"
          defaultOpened={isGroupActive(link)}
        >
          {link.children.map((child) => (
            <NavLink
              key={child.path}
              label={child.label}
              leftSection={child.icon}
              active={isActive(child.path)}
              onClick={() => { navigate(child.path); closeDrawer() }}
              color="grapefruit"
            />
          ))}
        </NavLink>
      )
    }
    return (
      <NavLink
        key={link.path}
        label={link.label}
        leftSection={link.icon}
        active={isActive(link.path)}
        onClick={() => { navigate(link.path); closeDrawer() }}
        color="grapefruit"
      />
    )
  })

  const avatarMenu = (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Avatar src={user?.avatar} radius="xl" size="sm" style={{ cursor: 'pointer' }} color="grapefruit" />
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{user?.user_name}</Menu.Label>
        <Menu.Item leftSection={<User size={14} />} onClick={() => navigate('/profile')}>Profile</Menu.Item>
        <Menu.Divider />
        <Menu.Item leftSection={<LogOut size={14} />} color="red" onClick={logout}>Logout</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )

  return (
    <>
      <Group h="100%" px="md" justify="space-between" wrap="nowrap">
        <Flex align="center" gap="sm" style={{ flexShrink: 0 }}>
          {user && <Burger opened={drawerOpened} onClick={openDrawer} size="sm" hiddenFrom="sm" />}
          <img
            src="/static/logo-small.png"
            alt="SAT Tutor"
            style={{ height: 40, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
        </Flex>

        {user && (
          <Group gap={0} visibleFrom="sm" wrap="nowrap" style={{ overflow: 'auto' }}>
            {desktopLinks}
          </Group>
        )}

        <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
          {user ? (
            <>
              {/* {user.role === 'student' && (
                <Badge variant="light" color="blue" size="lg" leftSection={<Zap size={14} />}>
                  {user.xp_total ?? 0} XP
                </Badge>
              )} */}
              {avatarMenu}
            </>
          ) : (
            <>
              <Button variant="outline" color="eveningSea" onClick={() => navigate('/login')}>Log in</Button>
            {/*   <Button color="grapefruit" onClick={() => navigate('/register')}>Start for free</Button> */}
            </>
          )}
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={() => toggleColorScheme()}
            title="Toggle color scheme"
            style={{ color: 'var(--sat-text-dim)' }}
          >
            {computed === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </ActionIcon>
        </Group>
      </Group>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="xs"
        padding="md"
        title={<img src="/static/logo-small.png" alt="SAT Tutor" style={{ height: 36 }} />}
      >
        {mobileLinks}
      </Drawer>
    </>
  )
}
